import { Button, Card, Chip, IconPlus } from "@heroui/react"
import { useState } from "react"

export interface KartuProdukData {
  id: string
  name: string
  imageUrl: string | null
  /** Sudah diformat pemanggil (mis. "Rp15.000" / "Mulai Rp18.000"). Kartu tak menghitung harga. */
  priceLabel: string
  /** true = punya varian/modifier → tombol membuka detail; false = quick-add langsung. */
  hasOptions: boolean
}

export interface KartuProdukProps {
  data: KartuProdukData
  /** Aksi tambah/pilih. Tanpa `onPilih` (atau dengan `readOnly`) kartu jadi pratinjau tanpa tombol. */
  onPilih?: (id: string) => void
  readOnly?: boolean
  /** Habis di outlet ini: kartu abu-abu dan tanpa tombol tambah (ADR-0017, keputusan rinci 5). */
  unavailable?: boolean
  /** Teks label habis dari kamus pemanggil (`@ramoo/i18n`); paket ini tidak memegang teks. */
  unavailableLabel?: string
  /** Jumlah produk ini di keranjang; > 0 mengganti tanda + di tombol dengan angkanya. */
  jumlah?: number
  /** Label pembaca layar tombolnya, dari kamus pemanggil. Bawaannya kalimat bahasa Indonesia. */
  labelTombol?: string
}

/** Fallback lokal netral saat produk tak berfoto / foto gagal — bukan placeholder remote (plan §0.1). */
const GambarKosong = () => (
  <span className="sok-kartu__gambar-kosong" aria-hidden="true">
    <svg
      viewBox="0 0 24 24"
      width="30"
      height="30"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <title>Tanpa gambar</title>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m21 16-5-5L5 20" />
    </svg>
  </span>
)

/** "Mulai Rp18.000" → kata "Mulai" redup, angkanya tebal; harga tunggal tebal seluruhnya. */
const Harga = ({ label }: { label: string }) => {
  const m = /^(\S+)\s+(Rp.*)$/.exec(label)
  return m ? (
    <span className="sok-kartu__harga">
      <span className="sok-kartu__harga-awal">{m[1]} </span>
      <b>{m[2]}</b>
    </span>
  ) : (
    <span className="sok-kartu__harga">
      <b>{label}</b>
    </span>
  )
}

/**
 * Kartu produk presentasional — dipakai katalog tamu DAN pratinjau owner supaya keduanya tidak
 * berbeda tanpa sengaja (plan PR5 §1). Rupanya dari design system "Ramoo": Card, Chip, dan Button
 * HeroUI; tata letaknya di `styles.css`.
 *
 * Bentuknya mengikuti mockup Modul 12: gambar 116 px, tombol bulat di pojok gambar (tanda + atau
 * jumlah di keranjang), nama satu baris, lalu harga. Dulu kartu 256 px dengan nama dua baris dan
 * "Mulai Rp …" yang patah di sebelah tombol "Pilih", sehingga cuma empat produk per layar.
 *
 * Warna aksen mengikuti `--accent` halaman (tamu: warna etalase pemilik), atau `--sok-primary`
 * kalau pembungkusnya memasangnya (pratinjau owner: warna draf). Komponen ini tidak memanggil API
 * dan tidak menghitung harga — harga final tetap dihitung server.
 */
export const KartuProduk = ({
  data,
  onPilih,
  readOnly,
  unavailable,
  unavailableLabel,
  jumlah = 0,
  labelTombol,
}: KartuProdukProps) => {
  const [gambarGagal, setGambarGagal] = useState(false)
  const adaGambar = !!data.imageUrl && !gambarGagal
  const bisaDipilih = !!onPilih && !readOnly && !unavailable
  return (
    <Card<"article">
      className={`sok-kartu${unavailable ? " sok-kartu--habis" : ""}`}
      render={(props) => <article {...props} />}
    >
      <div className="sok-kartu__gambar">
        {adaGambar ? (
          <img
            src={data.imageUrl ?? ""}
            alt=""
            loading="lazy"
            onError={() => setGambarGagal(true)}
          />
        ) : (
          <GambarKosong />
        )}
        {unavailable && unavailableLabel ? (
          <Chip className="sok-kartu__badge" color="danger" variant="soft" size="sm">
            {unavailableLabel}
          </Chip>
        ) : null}
        {bisaDipilih ? (
          <Button
            className="sok-kartu__tambah"
            isIconOnly={jumlah === 0}
            size="sm"
            aria-label={
              labelTombol ??
              (data.hasOptions ? `Pilih ${data.name}` : `Tambah ${data.name} ke pesanan`)
            }
            onPress={() => onPilih?.(data.id)}
          >
            {jumlah > 0 ? <span className="tabular-nums">{jumlah}</span> : <IconPlus />}
          </Button>
        ) : null}
      </div>
      <Card.Content className="sok-kartu__isi">
        <Card.Title className="sok-kartu__nama" title={data.name}>
          {data.name}
        </Card.Title>
        <Harga label={data.priceLabel} />
      </Card.Content>
    </Card>
  )
}
